import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Plus, Shield, ShieldOff, RefreshCw, Copy, Check, Mail, AlertTriangle, Clock, CheckCircle2, XCircle, Server, MoreHorizontal, Eye, Edit, KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DataTable } from '@/components/data/DataTable'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { schedulerApi, type Scheduler, type CreateSchedulerResponse } from '../api'

function copyToClipboard(text: string): boolean {
    try {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text)
            return true
        }
    } catch { /* fallback below */ }
    try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        return true
    } catch { return false }
}
import type { ColumnDef } from '@tanstack/react-table'

function StatusBadge({ status }: { status: string }) {
    const config: Record<string, { bg: string; text: string; icon: typeof CheckCircle2 }> = {
        Active: { bg: 'bg-green-100 dark:bg-green-950/30', text: 'text-green-700 dark:text-green-400', icon: CheckCircle2 },
        Pending: { bg: 'bg-amber-100 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', icon: Clock },
        Revoked: { bg: 'bg-red-100 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', icon: XCircle },
    }
    const c = config[status] || config.Pending
    const Icon = c.icon
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
            <Icon className="h-3 w-3" />{status}
        </span>
    )
}

export function SchedulersPage() {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const { formatDateTime } = useDateTimeFormat()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

    const [showAdd, setShowAdd] = useState(false)
    const [showToken, setShowToken] = useState<CreateSchedulerResponse | null>(null)
    const [addForm, setAddForm] = useState({ locationId: '', description: '' })
    const [copied, setCopied] = useState(false)
    const [tokenSaved, setTokenSaved] = useState(false) // Persistent flag — true once copied or emailed
    const [showTokenWarning, setShowTokenWarning] = useState(false)
    const [showView, setShowView] = useState<Scheduler | null>(null)
    const [showEdit, setShowEdit] = useState<Scheduler | null>(null)
    const [editForm, setEditForm] = useState({ description: '', locationId: '', ipAddress: '' })
    const [showConfirm, setShowConfirm] = useState<Scheduler | null>(null)
    const [confirmCode, setConfirmCode] = useState('')
    const [showRevoke, setShowRevoke] = useState<number | null>(null)

    // Fetch schedulers
    const { data: schedulersData, isLoading } = useQuery({
        queryKey: ['schedulers'],
        queryFn: async () => {
            const res = await schedulerApi.list()
            const d = res.data as any
            return (d?.data || []) as Scheduler[]
        },
        enabled: isAuthenticated,
    })

    // Fetch locations for dropdown (from Location table, Description column)
    const { data: locationsData } = useQuery({
        queryKey: ['scheduler-locations'],
        queryFn: async () => {
            const res = await schedulerApi.getLocations()
            const d = res.data as any
            return (d?.data || []) as Array<{ id: number; description: string }>
        },
        enabled: isAuthenticated && showAdd,
    })

    const schedulers = schedulersData || []
    const locations = locationsData || []

    // Create scheduler
    const createMut = useMutation({
        mutationFn: () => schedulerApi.create({
            locationId: addForm.locationId ? parseInt(addForm.locationId) : undefined,
            description: addForm.description || undefined,
        }),
        onSuccess: (res) => {
            const d = (res.data as any)?.data || res.data
            setShowAdd(false)
            setShowToken(d as CreateSchedulerResponse)
            setAddForm({ locationId: '', description: '' })
            queryClient.invalidateQueries({ queryKey: ['schedulers'] })
            toast({ title: 'Scheduler created' })
        },
        onError: () => toast({ title: 'Failed to create scheduler', variant: 'destructive' }),
    })

    // Revoke scheduler
    const revokeMut = useMutation({
        mutationFn: (id: number) => schedulerApi.revoke(id),
        onSuccess: () => {
            setShowRevoke(null)
            queryClient.invalidateQueries({ queryKey: ['schedulers'] })
            toast({ title: 'Scheduler revoked' })
        },
        onError: () => toast({ title: 'Failed to revoke scheduler', variant: 'destructive' }),
    })

    // Confirm scheduler registration
    const confirmMut = useMutation({
        mutationFn: ({ id, code }: { id: number; code: string }) =>
            (async () => { const api = (await import('@/lib/api/axios')).default; return api.post(`/api/schedulers/${id}/confirm`, { confirmationToken: code }); })(),
        onSuccess: () => {
            setShowConfirm(null)
            setConfirmCode('')
            queryClient.invalidateQueries({ queryKey: ['schedulers'] })
            toast({ title: 'Scheduler confirmed and activated' })
        },
        onError: () => toast({ title: 'Failed to confirm scheduler', variant: 'destructive' }),
    })

    // Regenerate token
    const regenMut = useMutation({
        mutationFn: (id: number) => schedulerApi.regenerateToken(id),
        onSuccess: (res) => {
            const d = (res.data as any)?.data || res.data
            setShowToken(d as CreateSchedulerResponse)
            queryClient.invalidateQueries({ queryKey: ['schedulers'] })
            toast({ title: 'Token regenerated' })
        },
        onError: () => toast({ title: 'Failed to regenerate token', variant: 'destructive' }),
    })

    const columns: ColumnDef<Scheduler>[] = [
        {
            accessorKey: 'description',
            header: 'Description',
            cell: ({ row }) => (
                <div className="text-sm font-medium">{row.original.description ? <TranslatableText text={row.original.description} /> : '-'}</div>
            ),
        },
        {
            accessorKey: 'locationName',
            header: 'Location',
            cell: ({ row }) => (
                <div className="text-sm">{row.original.locationName || '-'}</div>
            ),
        },
        {
            accessorKey: 'ipAddress',
            header: 'IP Address',
            cell: ({ row }) => row.original.ipAddress
                ? <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{row.original.ipAddress}</code>
                : <span className="text-sm text-muted-foreground">—</span>,
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => <StatusBadge status={row.original.status} />,
        },
        {
            accessorKey: 'lastSeen',
            header: 'Last Seen',
            cell: ({ row }) => row.original.lastSeen
                ? <div>
                    <div className="text-sm">{formatDateTime(row.original.lastSeen)}</div>
                    <div className="text-xs text-muted-foreground">{row.original.totalUploads} uploads</div>
                  </div>
                : <span className="text-sm text-muted-foreground">Never</span>,
        },
        {
            accessorKey: 'created',
            header: 'Created',
            cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created)}</span>,
        },
        {
            accessorKey: 'registered',
            header: 'Registered',
            cell: ({ row }) => row.original.registered
                ? <span className="text-sm">{formatDateTime(row.original.registered!)}</span>
                : <span className="text-sm text-muted-foreground">Not yet</span>,
        },
        {
            id: 'actions',
            header: '',
            size: 50,
            cell: ({ row }) => {
                const s = row.original
                return (
                    <div className="flex justify-end">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => setShowView(s)}>
                                    <Eye className="mr-2 h-4 w-4" />View Scheduler
                                </DropdownMenuItem>
                                {s.status !== 'Revoked' && (
                                    <DropdownMenuItem onClick={() => setShowEdit(s)}>
                                        <Edit className="mr-2 h-4 w-4" />Edit Scheduler
                                    </DropdownMenuItem>
                                )}
                                {s.status === 'Pending' && (
                                    <DropdownMenuItem onClick={() => { setShowConfirm(s); setConfirmCode('') }}>
                                        <CheckCircle2 className="mr-2 h-4 w-4" />Confirm Registration
                                    </DropdownMenuItem>
                                )}
                                {s.status === 'Pending' && s.tokenExpired && (
                                    <DropdownMenuItem onClick={() => regenMut.mutate(s.id)}>
                                        <KeyRound className="mr-2 h-4 w-4" />Regenerate Token
                                    </DropdownMenuItem>
                                )}
                                {s.status !== 'Revoked' && (
                                    <DropdownMenuItem className="text-destructive" onClick={() => setShowRevoke(s.id)}>
                                        <ShieldOff className="mr-2 h-4 w-4" />Revoke
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )
            },
        },
    ]

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Schedulers</h1>
                    <p className="text-muted-foreground">Manage on-premises and cloud schedulers</p>
                </div>
                <Button onClick={() => setShowAdd(true)}>
                    <Plus className="h-4 w-4 mr-2" />Add Scheduler
                </Button>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center h-32 text-muted-foreground">Loading...</div>
            ) : schedulers.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 border rounded-lg bg-muted/30">
                    <Server className="h-10 w-10 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">No schedulers registered</p>
                    <p className="text-xs text-muted-foreground mt-1">Add a scheduler to start ingesting backups</p>
                </div>
            ) : (
                <DataTable columns={columns} data={schedulers} searchKey="description" searchPlaceholder="Search schedulers..." />
            )}

            {/* Add Scheduler Dialog */}
            {showAdd && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Add Scheduler</h3>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm">Description</Label>
                                <Input
                                    value={addForm.description}
                                    onChange={(e) => setAddForm(f => ({ ...f, description: e.target.value }))}
                                    placeholder="e.g. Primary DC Scheduler, DR Site Scheduler"
                                    className="text-sm mt-1"
                                />
                                <p className="text-xs text-muted-foreground mt-1">A description to identify this scheduler. Useful when a location has multiple schedulers.</p>
                            </div>
                            <div>
                                <Label className="text-sm">Location</Label>
                                <select
                                    value={addForm.locationId}
                                    onChange={(e) => setAddForm(f => ({ ...f, locationId: e.target.value }))}
                                    className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">Select location...</option>
                                    {locations.map(l => (
                                        <option key={l.id} value={l.id}>{l.description}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button>
                            <Button size="sm" onClick={() => createMut.mutate()} disabled={createMut.isPending}>
                                {createMut.isPending ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Shield className="h-3.5 w-3.5 mr-1.5" />}
                                Create Scheduler
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Registration Token Display */}
            {showToken && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-lg w-full mx-4">
                        <h3 className="text-lg font-semibold mb-2">Registration Token</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Copy this token and paste it into the scheduler's registration screen (Settings → Registration).
                        </p>
                        <div>
                            <Label className="text-xs text-muted-foreground">Full Token</Label>
                            <textarea
                                readOnly
                                value={showToken.registrationToken}
                                className="w-full h-24 mt-1 rounded-md border bg-muted px-3 py-2 font-mono text-[10px] resize-none select-all"
                            />
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                            <Button variant="outline" size="sm" onClick={() => {
                                copyToClipboard(showToken.registrationToken)
                                setCopied(true)
                                setTokenSaved(true)
                            }}>
                                {copied ? <Check className="h-3.5 w-3.5 mr-1.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                                {copied ? 'Copied' : 'Copy to Clipboard'}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => {
                                const subject = encodeURIComponent('VaultSentinel Scheduler Registration Token')
                                const body = encodeURIComponent(
                                    `Please register this scheduler by pasting the following token into the scheduler's registration screen (Settings → Registration).\n\nToken:\n${showToken.registrationToken}\n\nThis token expires at ${formatDateTime(showToken.tokenExpiry)} and can only be used once.`
                                )
                                window.open(`mailto:?subject=${subject}&body=${body}`)
                                setTokenSaved(true)
                            }}>
                                <Mail className="h-3.5 w-3.5 mr-1.5" />Email to Engineer
                            </Button>
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 mt-4">
                            <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-yellow-700 dark:text-yellow-400">
                                This token expires in 4 hours and can only be used once. After registration, the private keys in this token will only exist on the scheduler.
                            </span>
                        </div>
                        <div className="flex justify-end mt-4">
                            <Button size="sm" onClick={() => {
                                if (!tokenSaved) {
                                    setShowTokenWarning(true)
                                } else {
                                    setShowToken(null); setCopied(false); setTokenSaved(false); setShowTokenWarning(false)
                                }
                            }}>Done</Button>
                        </div>
                        {showTokenWarning && (
                            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 mt-3">
                                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-red-700 dark:text-red-400">Token not saved</p>
                                    <p className="text-xs text-red-600 dark:text-red-400/80 mt-0.5">
                                        You haven't copied the token or emailed it. This token will not be available after closing this window. Are you sure?
                                    </p>
                                    <div className="flex gap-2 mt-2">
                                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowTokenWarning(false)}>
                                            Go Back
                                        </Button>
                                        <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => {
                                            setShowToken(null); setCopied(false); setTokenSaved(false); setShowTokenWarning(false)
                                        }}>
                                            Close Anyway
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* View Scheduler Dialog */}
            {showView && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Scheduler Details</h3>
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-[120px_1fr] gap-2">
                                <span className="text-muted-foreground">ID</span>
                                <span className="font-medium">{showView.id}</span>
                                <span className="text-muted-foreground">Description</span>
                                <span className="font-medium">{showView.description || '-'}</span>
                                <span className="text-muted-foreground">Location</span>
                                <span className="font-medium">{showView.locationName || '-'}</span>
                                <span className="text-muted-foreground">IP Address</span>
                                {showView.ipAddress
                                    ? <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{showView.ipAddress}</code>
                                    : <span>—</span>}
                                <span className="text-muted-foreground">Status</span>
                                <span><StatusBadge status={showView.status} /></span>
                                <span className="text-muted-foreground">Created</span>
                                <span>{formatDateTime(showView.created)}</span>
                                <span className="text-muted-foreground">Registered</span>
                                <span>{showView.registered ? formatDateTime(showView.registered) : 'Not yet'}</span>
                                <span className="text-muted-foreground">Last Seen</span>
                                <span>{showView.lastSeen ? formatDateTime(showView.lastSeen) : 'Never'}</span>
                                <span className="text-muted-foreground">Total Uploads</span>
                                <span>{showView.totalUploads || 0}</span>
                                <span className="text-muted-foreground">Scheduler UUID</span>
                                <code className="bg-muted px-1.5 py-0.5 rounded text-[10px] break-all">{showView.schedulerId}</code>
                            </div>
                        </div>
                        <div className="flex justify-end mt-4">
                            <Button size="sm" onClick={() => setShowView(null)}>Close</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Scheduler Dialog */}
            {showEdit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Edit Scheduler</h3>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm">Description</Label>
                                <Input
                                    defaultValue={showEdit.description}
                                    onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                                    className="text-sm mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-sm">Location</Label>
                                <select
                                    defaultValue={showEdit.locationId?.toString() || ''}
                                    onChange={(e) => setEditForm(f => ({ ...f, locationId: e.target.value }))}
                                    className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">Select location...</option>
                                    {locations.map(l => (
                                        <option key={l.id} value={l.id}>{l.description}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label className="text-sm">IP Address / CIDR</Label>
                                <Input
                                    defaultValue={showEdit.ipAddress || ''}
                                    onChange={(e) => setEditForm(f => ({ ...f, ipAddress: e.target.value }))}
                                    className="font-mono text-sm mt-1"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="outline" size="sm" onClick={() => setShowEdit(null)}>Cancel</Button>
                            <Button size="sm" onClick={async () => {
                                try {
                                    await (await import('@/lib/api/axios')).default.put(`/api/schedulers/${showEdit.id}`, {
                                        description: editForm.description || showEdit.description,
                                        locationId: editForm.locationId ? parseInt(editForm.locationId) : showEdit.locationId,
                                        ipAddress: editForm.ipAddress || showEdit.ipAddress,
                                    })
                                    setShowEdit(null)
                                    queryClient.invalidateQueries({ queryKey: ['schedulers'] })
                                    toast({ title: 'Scheduler updated' })
                                } catch {
                                    toast({ title: 'Failed to update', variant: 'destructive' })
                                }
                            }}>
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Registration Dialog */}
            {showConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-lg w-full mx-4">
                        <h3 className="text-lg font-semibold mb-2">Confirm Registration</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            After the engineer registered the scheduler using your token, the scheduler generated a confirmation token. Paste that token below to verify the registration.
                        </p>
                        <div className="mb-4">
                            <Label className="text-sm">Confirmation Token</Label>
                            <textarea
                                value={confirmCode}
                                onChange={(e) => setConfirmCode(e.target.value)}
                                placeholder="Paste the confirmation token from the scheduler..."
                                className="w-full h-24 mt-1 rounded-md border bg-background px-3 py-2 font-mono text-[10px] resize-none"
                            />
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 mb-4">
                            <Shield className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-blue-700 dark:text-blue-400">
                                This token confirms the scheduler successfully decoded the registration token, generated its machine fingerprint, and is ready to upload backups. The scheduler will not be able to upload until this step is completed.
                            </span>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowConfirm(null)}>Cancel</Button>
                            <Button size="sm" disabled={!confirmCode.trim() || confirmMut.isPending}
                                onClick={() => confirmMut.mutate({ id: showConfirm.id, code: confirmCode.trim() })}>
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Verify & Activate
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Revoke Confirmation */}
            {showRevoke && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-sm mx-4">
                        <h3 className="text-lg font-semibold mb-2 text-destructive">Revoke Scheduler</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            This will revoke the scheduler's signing key and prevent it from uploading. This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowRevoke(null)}>Cancel</Button>
                            <Button variant="destructive" size="sm" onClick={() => revokeMut.mutate(showRevoke)} disabled={revokeMut.isPending}>
                                <ShieldOff className="h-3.5 w-3.5 mr-1.5" />Revoke
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
